/**
 * Export utility functions for CSV and JSON export
 */

/**
 * Convert array of objects to CSV string
 * @param {Array} data - Array of objects to convert
 * @param {Array} columns - Array of column definitions with accessorKey and header
 * @returns {string} CSV string
 */
export const convertToCSV = (data, columns = null) => {
  if (!data || data.length === 0) {
    return '';
  }

  // If columns are provided, use them; otherwise infer from first object
  const headers = columns 
    ? columns.map(col => col.header || col.accessorKey)
    : Object.keys(data[0]);

  // Create CSV header row
  const csvHeaders = headers.map(header => `"${String(header).replace(/"/g, '""')}"`).join(',');

  // Create CSV data rows
  const csvRows = data.map(row => {
    if (columns) {
      // Use column accessors to extract values
      return columns.map(col => {
        const value = col.accessorKey.split('.').reduce((obj, key) => obj?.[key], row);
        const formattedValue = col.cell 
          ? col.cell({ getValue: () => value, row: { original: row } })
          : value;
        return `"${String(formattedValue || '').replace(/"/g, '""')}"`;
      }).join(',');
    } else {
      // Use object keys directly
      return headers.map(header => {
        const value = row[header];
        return `"${String(value || '').replace(/"/g, '""')}"`;
      }).join(',');
    }
  });

  return [csvHeaders, ...csvRows].join('\n');
};

/**
 * Download data as CSV file
 * @param {Array} data - Data to export
 * @param {string} filename - Filename without extension
 * @param {Array} columns - Optional column definitions
 */
export const downloadCSV = (data, filename, columns = null) => {
  const csv = convertToCSV(data, columns);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Download data as JSON file
 * @param {Object|Array} data - Data to export
 * @param {string} filename - Filename without extension
 * @param {Object} metadata - Optional metadata to include
 */
export const downloadJSON = (data, filename, metadata = null) => {
  const exportData = {
    exported_at: new Date().toISOString(),
    ...(metadata && { metadata }),
    data: data,
  };

  const json = JSON.stringify(exportData, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Export revenue data
 * @param {Object} revenueData - Revenue data object with summary, chart, trends, breakdowns
 * @param {string} format - 'csv' or 'json'
 * @param {Object} filters - Current filters (dateRange, accountId, etc.)
 */
export const exportRevenueData = (revenueData, format, filters = {}) => {
  const { summary, chartData, trends, breakdowns } = revenueData;
  const metadata = {
    type: 'revenue',
    date_range: filters.dateRange || {},
    account_id: filters.accountId || null,
    exported_at: new Date().toISOString(),
  };

  if (format === 'csv') {
    // Export main data as CSV
    const allData = [];
    
    // Add summary as first row
    if (summary) {
      allData.push({
        type: 'Summary',
        total_revenue: summary.total_revenue || 0,
        total_impressions: summary.total_impressions || 0,
        average_ecpm: summary.average_ecpm || 0,
        total_clicks: summary.total_clicks || 0,
      });
      allData.push({}); // Empty row separator
    }

    // Add chart/trends data
    if (trends?.trends && trends.trends.length > 0) {
      allData.push({ type: 'Trends Data' });
      trends.trends.forEach(trend => {
        allData.push({
          date: trend.date || '',
          revenue: trend.revenue || 0,
          impressions: trend.impressions || 0,
          clicks: trend.clicks || 0,
          ecpm: trend.ecpm || 0,
          moving_average: trend.moving_average || 0,
        });
      });
      allData.push({}); // Empty row separator
    }

    // Add breakdowns
    ['apps', 'adUnits', 'countries', 'adSources'].forEach(breakdownType => {
      const breakdown = breakdowns[breakdownType] || [];
      if (breakdown.length > 0) {
        allData.push({ type: `Breakdown by ${breakdownType}` });
        breakdown.forEach(item => {
          allData.push({
            breakdown_value: item.breakdown_value || '',
            revenue: item.revenue || 0,
            revenue_percent: item.revenue_percent || 0,
            impressions: item.impressions || 0,
            clicks: item.clicks || 0,
            ecpm: item.ecpm || 0,
          });
        });
        allData.push({}); // Empty row separator
      }
    });

    downloadCSV(allData, 'revenue-export');
  } else {
    // Export as JSON
    downloadJSON(
      {
        summary,
        chart_data: chartData,
        trends,
        breakdowns,
      },
      'revenue-export',
      metadata
    );
  }
};

/**
 * Export performance data
 * @param {Object} performanceData - Performance data object with summary, trends, metrics
 * @param {string} format - 'csv' or 'json'
 * @param {Object} filters - Current filters (dateRange, accountId, etc.)
 */
export const exportPerformanceData = (performanceData, format, filters = {}) => {
  const { summary, trends, metricsData } = performanceData;
  const metadata = {
    type: 'performance',
    date_range: filters.dateRange || {},
    account_id: filters.accountId || null,
    exported_at: new Date().toISOString(),
  };

  if (format === 'csv') {
    const allData = [];
    
    // Add summary
    if (summary) {
      allData.push({
        type: 'Summary',
        total_impressions: summary.total_impressions || 0,
        total_clicks: summary.total_clicks || 0,
        average_ctr: summary.average_ctr || 0,
        average_fill_rate: summary.average_fill_rate || 0,
        average_completion_rate: summary.average_completion_rate || 0,
        total_active_users: summary.total_active_users || 0,
        total_sessions: summary.total_sessions || 0,
        impressions_per_session: summary.impressions_per_session || 0,
      });
      allData.push({}); // Empty row separator
    }

    // Add trends data
    if (trends?.trends && trends.trends.length > 0) {
      allData.push({ type: 'Trends Data' });
      trends.trends.forEach(trend => {
        allData.push({
          date: trend.date || '',
          impressions: trend.impressions || 0,
          clicks: trend.clicks || 0,
          ctr: trend.ctr || 0,
          fill_rate: trend.fill_rate || 0,
          completion_rate: trend.completion_rate || 0,
          active_users: trend.active_users || 0,
          sessions: trend.sessions || 0,
        });
      });
      allData.push({}); // Empty row separator
    }

    // Add detailed metrics data
    if (metricsData && metricsData.length > 0) {
      allData.push({ type: 'Detailed Metrics' });
      metricsData.forEach(row => {
        allData.push({
          date: row.date || '',
          app_key: row.app_key || '',
          ad_unit: row.ad_unit || '',
          country: row.country || '',
          ad_source: row.ad_source || '',
          impressions: row.impressions || 0,
          clicks: row.clicks || 0,
          ctr: row.ctr || 0,
          fill_rate: row.fill_rate || 0,
          completion_rate: row.completion_rate || 0,
          active_users: row.active_users || 0,
          sessions: row.sessions || 0,
        });
      });
    }

    downloadCSV(allData, 'performance-export');
  } else {
    // Export as JSON
    downloadJSON(
      {
        summary,
        trends,
        metrics: metricsData,
      },
      'performance-export',
      metadata
    );
  }
};
